import { CheckCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from '@tanstack/react-router';
import { ActionButton, BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import { featureFlags } from '_/constants/loginPools.constants';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useLoginPoolContext } from '_/contexts/LoginPoolContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useLogins, useDeleteLogin } from '_/hooks/useLogins';
import useOrgAccess from '_/hooks/useOrgAccess';
import { ILoginRow } from '_/types/logins/logins.types';
import { compareSort } from '_/utils/formatUtils';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Modal, Button } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import React, { ReactElement, useState, useMemo, useCallback, useEffect } from 'react';

const Logins = (): ReactElement => {
  const history = useNavigate();
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { selectedOrgSlug: orgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug: projectSlug } = useProject();
  const { selectedLoginPool, selectedLoginPoolSlug: loginPoolSlug } = useLoginPoolContext();
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [idToDelete, setIdToDelete] = useState('');

  // Fetch data
  const loginPoolId = selectedLoginPool?.id || '';
  const { data: loginsData = [], refetch, isLoading, isRefetching } = useLogins(loginPoolId, !!loginPoolId);
  const deleteMutation = useDeleteLogin();

  // Transform logins data for table
  const logins = useMemo(
    () =>
      loginsData.map((login) => ({
        id: login.id,
        name: login.name,
        active: login.active,
        loginPoolId: login.loginPoolId,
        href: `/orgs/${orgSlug}/projects/${projectSlug}/loginpools/${loginPoolSlug}/logins/${login.id}`,
        snapshotId: login.snapshotId,
        snapshotHref: login.snapshotId
          ? `/orgs/${orgSlug}/projects/${projectSlug}/collections/${login.snapshot?.source?.collection?.slug}/sources/${login.snapshot?.source?.slug}/snapshots/${login.snapshotId}/home`
          : '',
        lastUsed: login.lastUsed,
      })),
    [loginsData, orgSlug, projectSlug, loginPoolSlug],
  );

  // Event handlers
  const handleNewLogin = useCallback(() => {
    void history({
      to: `/orgs/${orgSlug}/projects/${projectSlug}/loginpools/${loginPoolSlug}/logins/_new`,
    });
  }, [history, orgSlug, projectSlug, loginPoolSlug]);

  const handleDelete = useCallback((id: string) => {
    setIdToDelete(id);
    setDeleteModalVisible(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (idToDelete && loginPoolId) {
      deleteMutation.mutate({ loginId: idToDelete, loginPoolId: loginPoolId });
    }
    setDeleteModalVisible(false);
    setIdToDelete('');
  }, [idToDelete, loginPoolId, deleteMutation]);

  const handleCancelDelete = useCallback(() => {
    setDeleteModalVisible(false);
    setIdToDelete('');
  }, []);

  // Table columns
  const columns: ColumnType<ILoginRow>[] = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, row: ILoginRow) => (
          <Link to={row.href} className="wb-textDecoration-underline">
            {name}
          </Link>
        ),
        sorter: compareSort('name'),
      },
      {
        title: 'Snapshot',
        dataIndex: 'snapshotId',
        key: 'snapshotId',
        render: (snapshotId: string, row: ILoginRow) =>
          snapshotId && (
            <Link to={row.snapshotHref} className="wb-textDecoration-underline" target="_blank">
              View
            </Link>
          ),
      },
      {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        render: (active: boolean) => active && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />,
      },
      {
        title: 'Last used',
        dataIndex: 'lastUsed',
        key: 'lastUsed',
        sorter: compareSort('lastUsed'),
      },
      {
        title: 'Action',
        dataIndex: 'id',
        key: 'id',
        render: (id: string) => <Button onClick={() => handleDelete(id)}>Delete</Button>,
      },
    ],
    [handleDelete],
  );

  // Action buttons
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      {
        onClick: handleNewLogin,
        icon: <PlusCircleOutlined />,
        label: 'Add Login',
        tooltip: access ? undefined : 'You do not have the right permissions to add a login',
        disabled: !access,
      },
    ];

    if (featureFlags.bulkReplace) {
      buttons.push({
        onClick: () => alert('Bulk Replace'),
        icon: <PlusCircleOutlined />,
        label: 'Bulk Replace',
        tooltip: access ? undefined : 'You do not have the right permissions to logins bulk replace',
        disabled: !access,
      });
    }

    return buttons;
  }, [access, handleNewLogin]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: selectedProject?.name || '', href: `/orgs/${orgSlug}/projects/${projectSlug}/home` },
      { title: 'Login Pools', href: `/orgs/${orgSlug}/projects/${projectSlug}/loginpools` },
    ];

    if (selectedLoginPool) {
      breadcrumbs.push(
        {
          title: selectedLoginPool.slug || '',
          href: `/orgs/${orgSlug}/projects/${projectSlug}/loginpools/${loginPoolSlug}`,
        },
        { title: 'Logins' },
      );
    }

    setBreadcrumbs(breadcrumbs);
    setActionButtons(actionButtons);
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetching);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    setOnRefresh,
    setRefreshLoading,
    selectedOrganization,
    orgSlug,
    selectedProject,
    projectSlug,
    selectedLoginPool,
    loginPoolSlug,
    actionButtons,
    handleRefresh,
    isRefetching,
  ]);

  return (
    <>
      <Table columns={columns} dataSource={logins} rowKey="id" loading={isLoading} />

      <Modal open={deleteModalVisible} title="Delete Login" onCancel={handleCancelDelete} onOk={handleConfirmDelete}>
        <p>Are you sure that you want to delete this login?</p>
      </Modal>
    </>
  );
};

export default Logins;
